import { useLocation, useParams } from 'react-router-dom';

import type { AssignmentFormData } from '../../types/assignmentTypes';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

export const usePreviewStatus = () => {
  const { watch } = useFormContext<AssignmentFormData>();
  const { id } = useParams();
  const location = useLocation();
  const isInstanceCreation = location.pathname.includes('instance');
  const {
    stepIndex,
    activeForm,
    setPreviewStatusMap,
    settingsFormVisited,
    notificationsFormVisited,
    audienceFormVisited,
  } = useAdminAssignmentsStore();

  const { simpleType, settings, audience, formBoundType } = watch();

  const { type, responses } = settings;

  const stepIndexMap = isInstanceCreation
    ? {
        audience: 0,
        content: 1,
        settings: 2,
        notifications: 3,
      }
    : {
        generalDetails: 0,
        automations: 1,
        audience: simpleType === 'automated' ? 2 : 1,
        content: simpleType === 'automated' ? 3 : 2,
        settings: simpleType === 'automated' ? 4 : 3,
        notifications: simpleType === 'automated' ? 5 : 4,
      };

  const getAutomationsStatus = () => {
    if (isInstanceCreation) {
      return 'notStarted';
    }

    const automationsStep = (stepIndexMap as { automations: number }).automations;

    if (stepIndex < automationsStep) {
      return !formBoundType || formBoundType === WorkflowType.PointInTime
        ? 'notStarted'
        : 'completed';
    }

    return stepIndex === automationsStep ? 'inProgress' : 'completed';
  };

  const getAudienceStatus = () => {
    if (type !== WorkflowType.PointInTime) {
      if (stepIndex === stepIndexMap.audience) {
        return 'inProgress';
      }
      return audienceFormVisited ? 'completed' : 'notStarted';
    }

    if (audience.length > 0) {
      return stepIndex === stepIndexMap.audience ? 'inProgress' : 'completed';
    }

    return stepIndex < stepIndexMap.audience ? 'notStarted' : 'inProgress';
  };

  const getContentStatus = () => {
    if (responses.length > 0) {
      return stepIndex === stepIndexMap.content ? 'inProgress' : 'completed';
    }

    return stepIndex < stepIndexMap.content ? 'notStarted' : 'inProgress';
  };

  const getSettingsStatus = () => {
    if (stepIndex === stepIndexMap.settings) {
      return 'inProgress';
    }

    return settingsFormVisited || isInstanceCreation ? 'completed' : 'notStarted';
  };

  const getNotificationsStatus = () => {
    if (stepIndex === stepIndexMap.notifications) {
      return 'inProgress';
    }
    return notificationsFormVisited || isInstanceCreation ? 'completed' : 'notStarted';
  };

  const automationsStatus = getAutomationsStatus();
  const audienceStatus = getAudienceStatus();
  const contentStatus = getContentStatus();
  const settingsStatus = getSettingsStatus();
  const notificationsStatus = getNotificationsStatus();

  useEffect(() => {
    setPreviewStatusMap({
      generalDetails: isInstanceCreation ? 'notStarted' : id ? 'completed' : 'inProgress',
      automations: isInstanceCreation ? 'notStarted' : id ? 'completed' : automationsStatus,
      audience: id ? 'completed' : audienceStatus,
      content: id ? 'completed' : contentStatus,
      settings: id ? 'completed' : settingsStatus,
      notifications: id ? 'completed' : notificationsStatus,
    });
  }, [
    activeForm,
    stepIndex,
    formBoundType,
    type,
    isInstanceCreation,
    id,
    automationsStatus,
    audienceStatus,
    contentStatus,
    settingsStatus,
    notificationsStatus,
  ]);
};
